import { useEffect, useId, useRef } from 'react'
import { streamTypeahead } from 'actions/SearchActions'
import { HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import useRecommendationPlaceIds from './useRecommendationPlaceIds'
import useTypeaheadRollout from './useTypeaheadRollout'

interface UseTypeaheadExtractionRefreshParams {
  isExtracting: boolean
  typeaheadId: string | undefined
  query: string | undefined
  typeaheadTypes?: Array<App.SearchPlaceType>
  rollout?: Array<string>
}

/**
 * Dispatches an extraction request once the results page mounts with
 * extracting=true. Covers a page refresh mid-extraction, and a natural-language
 * echo suggestion that reuses a typeaheadId whose streaming data may already exist.
 */
export function useTypeaheadExtractionRefresh({
  isExtracting,
  typeaheadId,
  query,
  typeaheadTypes = HOTEL_SEARCH_TYPEAHEAD_TYPES,
  rollout,
}: UseTypeaheadExtractionRefreshParams): void {
  const dispatch = useAppDispatch()
  const streamingData = useAppSelector(state =>
    typeaheadId ? state.search.typeaheadStreamingData[typeaheadId] : undefined,
  )
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const recentPlaceIds = useRecommendationPlaceIds()
  const effectiveRollout = useTypeaheadRollout(typeaheadTypes, rollout)
  const streamingListenerId = useId()
  const hasDispatchedRef = useRef(false)

  useEffect(() => {
    const hasMatchingCompletedData = streamingData?.isComplete && streamingData.completedInput === query
    // !streamingData alone would miss the echo-suggestion case: streamingData
    // may already exist from a preview or a different query under the same ID.
    if (isExtracting && typeaheadId && query && !hasMatchingCompletedData && !hasDispatchedRef.current) {
      hasDispatchedRef.current = true
      dispatch(
        streamTypeahead({
          input: query,
          typeaheadId,
          listenerId: streamingListenerId,
          types: typeaheadTypes,
          limit: 9,
          isLuxPlusMember: canViewLuxPlusBenefits,
          rollout: effectiveRollout,
          recentPlaceIds,
        }),
      )
    }

    // Reset the ref when typeaheadId changes or extraction completes
    if (!isExtracting || !typeaheadId) {
      hasDispatchedRef.current = false
    }
  }, [isExtracting, typeaheadId, query, streamingData, dispatch, canViewLuxPlusBenefits, streamingListenerId, typeaheadTypes, effectiveRollout, recentPlaceIds])
}
